import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen } from "lucide-react";
import { usePage, useArticles, formatDate } from "@/lib/content";
import { Reveal } from "@/components/site/Motion";
import { PageHero, CTABand, usePageTitle } from "@/components/site/Bits";

export default function Conteudo() {
  usePageTitle("Conteúdo Jurídico");
  const { sections: s } = usePage("conteudo");
  const { loading, items } = useArticles(24);
  const [category, setCategory] = useState("Todos");

  const categories = ["Todos", ...new Set(items.map((a) => a.category).filter(Boolean))];
  const filtered = category === "Todos" ? items : items.filter((a) => a.category === category);

  return (
    <>
      <PageHero
        title={s.hero_title || "Conteúdo Jurídico"}
        text={s.hero_text || "Artigos, orientações educativas e posts jurídicos para apoiar decisões com informação qualificada."}
        overline="Silva Cotan e Associados — Assessoria Jurídica"
      />

      <section className="bg-ivory">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          {categories.length > 1 && (
            <div className="mb-12 flex flex-wrap gap-3" data-testid="conteudo-filters">
              {categories.map((cat) => (
                <button
                  key={cat}
                  data-testid={`conteudo-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setCategory(cat)}
                  className={`border px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.18em] transition-colors ${
                    category === cat ? "border-gold bg-gold text-navy" : "border-navy/20 text-navy/70 hover:border-gold hover:text-gold-deep"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-gold border-t-transparent" aria-label="Carregando" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-5 border border-dashed border-navy/20 px-8 py-24 text-center" data-testid="conteudo-empty">
              <BookOpen size={36} className="text-gold-deep" strokeWidth={1.25} />
              <p className="max-w-md font-display text-2xl text-navy">Em breve, novos conteúdos serão publicados por aqui.</p>
              <p className="max-w-md text-sm text-inkgray">Acompanhe esta página para receber artigos e orientações educativas do escritório.</p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((a, i) => (
                <Reveal key={a.id} delay={0.05 * (i % 3)}>
                  <Link
                    to={`/conteudo-juridico/${a.slug}`}
                    data-testid={`conteudo-card-${a.slug}`}
                    className="group flex h-full flex-col border border-navy/10 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-gold"
                  >
                    {a.cover_url ? (
                      <div className="aspect-[16/10] overflow-hidden">
                        <img src={a.cover_url} alt={a.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                      </div>
                    ) : (
                      <div className="aspect-[16/10] overflow-hidden bg-navy">
                        <img src="/media/capa-padrao.jpg" alt="" className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-[1.03]" />
                      </div>
                    )}
                    <div className="flex flex-1 flex-col p-7">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gold-deep">
                        {a.category || "Conteúdo"} · {formatDate(a.published_at || a.created_at)}
                      </p>
                      <h2 className="mt-3 font-display text-xl leading-snug text-navy group-hover:text-gold-deep">{a.title}</h2>
                      <p className="mt-3 line-clamp-3 flex-1 text-sm leading-relaxed text-inkgray">
                        {(a.body || "").replace(/\s+/g, " ").slice(0, 140)}…
                      </p>
                      <span className="mt-5 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-navy transition-colors group-hover:text-gold-deep">
                        Ler artigo <ArrowUpRight size={13} />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTABand title="Dúvidas sobre algum conteúdo? Fale com a gente." button="Fale conosco" />
    </>
  );
}
